import { Popover } from 'radix-ui';
import { type KeyboardEvent, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { fieldVariants } from './fieldStyles';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  /** Выбранное значение справочника ('' — не выбрано). */
  value: string;
  onValueChange: (value: string) => void;
  options: readonly ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  name?: string;
  className?: string;
  /** Пользователь ушёл из поля, оставив текст, которого нет в справочнике. */
  onUnmatched?: (text: string) => void;
  onBlur?: () => void;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
}

const MAX_SUGGESTIONS = 6;

function normalize(text: string): string {
  return text.trim().toLocaleLowerCase();
}

/**
 * Combo со справочником (README «Поля форм → combo»): ввод с подсказками, значение — только из
 * справочника; точное совпадение отмечается «из справочника» (status-success 11).
 */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  invalid,
  id,
  name,
  className,
  onUnmatched,
  onBlur,
  ...aria
}: ComboboxProps) {
  const { t } = useTranslation('form');
  const listId = useId();
  const selected = options.find((option) => option.value === value) ?? null;
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const text = query ?? selected?.label ?? '';

  const suggestions = useMemo(() => {
    const needle = normalize(text);
    if (!needle || selected?.label === text) return [];
    return options
      .filter((option) => normalize(option.label).includes(needle))
      .slice(0, MAX_SUGGESTIONS);
  }, [options, text, selected]);

  const showList = open && suggestions.length > 0;
  const activeOption = showList
    ? suggestions[Math.min(activeIndex, suggestions.length - 1)]
    : undefined;

  function pick(option: ComboboxOption) {
    onValueChange(option.value);
    setQuery(null);
    setOpen(false);
  }

  function handleInput(nextText: string) {
    setQuery(nextText);
    setOpen(true);
    setActiveIndex(0);
    const exact = options.find((option) => normalize(option.label) === normalize(nextText));
    if (exact) {
      onValueChange(exact.value);
    } else if (value !== '') {
      onValueChange('');
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' && activeOption) {
      event.preventDefault();
      pick(activeOption);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function handleBlur() {
    setOpen(false);
    if (query !== null && normalize(query) !== '' && !selected) onUnmatched?.(query);
    if (selected) setQuery(null);
    onBlur?.();
  }

  return (
    <Popover.Root open={showList} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div className={cn('relative', className)}>
          <input
            id={id}
            name={name}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={listId}
            aria-activedescendant={
              activeOption ? `${listId}-${suggestions.indexOf(activeOption)}` : undefined
            }
            aria-invalid={invalid || undefined}
            value={text}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(event) => handleInput(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className={cn(fieldVariants({ size: 'md' }), selected ? 'pr-110' : undefined)}
            {...aria}
          />
          {selected ? (
            <span className="pointer-events-none absolute top-9 right-10 text-[11px] text-status-success">
              {t('combo.fromDictionary')}
            </span>
          ) : null}
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={2}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-60 w-(--radix-popover-trigger-width) overflow-hidden rounded-16 border border-line-strong bg-paper-pure shadow-dropdown animate-rise-menu"
        >
          <div id={listId} role="listbox">
            {suggestions.map((option, index) => (
              <div
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                tabIndex={-1}
                aria-selected={index === activeIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') pick(option);
                }}
                className={cn(
                  'cursor-pointer border-b border-line-soft px-10 py-7 text-[13px] text-ink-900 last:border-b-0 hover:bg-surface-sunken',
                  index === activeIndex ? 'bg-surface-sunken' : undefined,
                )}
              >
                {option.label}
              </div>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
